import { useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useInfiniteQuery } from "react-query";
import { ApiResponse } from "../../typings/api";
import { authenticatedRequest } from "../../services/api.service";
import { CompetencyTestType } from "../../typings/competency-test";
import CompetencyCard from "../../components/Layout/Competency/CompetencyCard";
import { useDispatch } from "react-redux";
import { LOADING_UI, UNLOADING_UI } from "../../redux/types";
import { ArrowDown, SearchX } from "lucide-react";
import Button from "../../components/UI/Button";
import { Card, CardContent } from "../../components/UI/Card";
import { COMPETENCY_KART, COMPETENCY_TDNA, COMPETENCY_TKA } from "../../utils/competency";

const CompetencyCat = () => {
  const { ref, inView } = useInView();
  const dispatch = useDispatch();
  const params: any = useParams();
  const [category, setCategory] = useState<string | null | undefined>(null);
  const { data, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery<ApiResponse>(
      ["competency-cat-list-page", category],
      async ({ pageParam = 1 }) => {
        try {
          const params: any = {
            page: pageParam,
            wc: 1
          };
          if (category) {
            params["code"] = category;
          }
          const response = await authenticatedRequest(
            "POST",
            "/competency-test/list",
            params
          );
          return response as ApiResponse;
        } catch (e) {
          return [] as unknown as ApiResponse;
        }
      },
      {
        getPreviousPageParam: (firstPage: any) =>
          firstPage?.meta?.previous_page_url
            ? Number(firstPage.meta.previous_page_url.replace("/?page=", ""))
            : undefined,
        getNextPageParam: (firstPage: any) =>
          firstPage?.meta?.next_page_url
            ? Number(firstPage.meta.next_page_url.replace("/?page=", ""))
            : undefined,
        enabled: false,
      }
    );
  useEffect(() => {
    if (isFetchingNextPage) {
      dispatch({ type: LOADING_UI });
    } else {
      dispatch({ type: UNLOADING_UI });
    }
  }, [isFetchingNextPage, dispatch]);
  useEffect(() => {
    if (params?.cat) {
      setCategory(params?.cat);
    }
  }, [params?.cat]);
  useEffect(() => {
    if (category) {
      fetchNextPage();
    }
  }, [category]);
  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView]);
  const HeaderKART = () => {
    return (
      <>
        <h5 className="text-2xl font-bold mb-5 text-center text-foreground">
          Kelase Academic Readiness Test
        </h5>
        <Card className="p-4">
          <p className="text-gray-700 mb-3">
            <strong>Kelase Academic Readiness Test (ART)</strong> adalah asesmen diagnostik yang
            dirancang untuk mengukur kesiapan akademik siswa melalui tiga
            kompetensi inti, yaitu Matematika Dasar, Bahasa Indonesia, dan
            Bahasa Inggris. Tes ini membantu memetakan kekuatan dan area
            pengembangan siswa sebagai fondasi pembelajaran lanjutan serta
            perencanaan karier yang lebih terarah. Hasil ART digunakan oleh
            sistem <strong>Kelase Career Mentor</strong> berbasis AI untuk memberikan rekomendasi
            jalur belajar, penguatan kompetensi, dan pengembangan karier yang
            sesuai dengan potensi dan kebutuhan setiap siswa.
          </p>
          <div className={"w-full flex justify-center mt-5"}>
            <Button
              primary
              block
              className="gradient-primary font-semibold text-sm"
              onClick={() => {
                const targetElement = document.querySelector(
                  "#competency-cat-list"
                );
                // Scroll the element into view with a smooth animation
                if (targetElement) {
                  const targetPosition =
                    (targetElement as any)?.offsetTop - 141;
                  window.scrollTo({
                    top: targetPosition,
                    behavior: "smooth",
                  });
                }
              }}
            >
              Mulai Sekarang <ArrowDown />
            </Button>
          </div>
        </Card>
      </>
    );
  }
  const HeaderTDNA = () => {
    return (
      <>
        <h5 className="text-2xl font-bold mb-5 text-center text-foreground">
          Kelase ThinkingDNA
        </h5>
        <Card className="p-4">
          <p className="text-gray-700 mb-3">
            <strong>Kelase ThinkingDNA</strong> adalah assessment untuk memetakan cara berpikir
            inti seseorang melalui tiga dimensi utama:{" "}
            <strong>Verbal Reasoning</strong>,
            <strong>Logical/Abstract Reasoning</strong>, dan{" "}
            <strong>Quantitative Reasoning</strong>. Assessment ini membantu
            memahami bagaimana seseorang memproses informasi, memecahkan
            masalah, dan mengambil keputusan, sehingga menjadi dasar bagi
            rekomendasi arah belajar, pilihan jurusan, dan kesiapan karier yang
            lebih personal dan berbasis data.
          </p>
          <div className={"w-full flex justify-center mt-5"}>
            <Button
              primary
              block
              className="gradient-primary font-semibold text-sm"
              onClick={() => {
                const targetElement = document.querySelector(
                  "#competency-cat-list"
                );
                // Scroll the element into view with a smooth animation
                if (targetElement) {
                  const targetPosition =
                    (targetElement as any)?.offsetTop - 141;
                  window.scrollTo({
                    top: targetPosition,
                    behavior: "smooth",
                  });
                }
              }}
            >
              Mulai Sekarang <ArrowDown />
            </Button>
          </div>
        </Card>
      </>
    );
  }
  const HeaderTKA = () => {
    return (
      <>
        <h5 className="text-2xl font-bold mb-5 text-center text-foreground">
          Try Out Tes Kemampuan Akademik 2026
        </h5>
        <Card className="p-4">
          <p className="text-gray-700 mb-3">
            <strong>Try Out Tes Kemampuan Akademik (TKA)</strong> adalah wahana
            latihan bagi siswa SD & SMP untuk mengerjakan soal-soal TKA yang disusun
            berdasarkan silabus resmi Kemendikbud. Melalui try out ini, siswa
            dapat mengukur kemampuan akademik, memahami pola soal, dan
            mempersiapkan diri dengan lebih percaya diri menghadapi tes yang
            sesungguhnya.
          </p>
          <div className={"w-full flex justify-center mt-5"}>
            <Button
              primary
              block
              className="gradient-primary font-semibold text-sm"
              onClick={() => {
                const targetElement = document.querySelector(
                  "#competency-cat-list"
                );
                // Scroll the element into view with a smooth animation
                if (targetElement) {
                  const targetPosition =
                    (targetElement as any)?.offsetTop - 141;
                  window.scrollTo({
                    top: targetPosition,
                    behavior: "smooth",
                  });
                }
              }}
            >
              Mulai Try Out TKA <ArrowDown />
            </Button>
          </div>
        </Card>
      </>
    );
  };
  const renderHeader = () => {
    switch (category) {
      case COMPETENCY_KART:
        return <HeaderKART />;
      case COMPETENCY_TDNA:
        return <HeaderTDNA />;
      case COMPETENCY_TKA:
        return <HeaderTKA />;
      default:
        return null;
    }
  };
  return (
    <>
      {renderHeader()}
      <h5 className="text-xl font-bold mb-3 text-foreground">
        Daftar Tes
      </h5>
      {data?.pages && (
        <div id="competency-cat-list">
          {data.pages[0]?.data?.length > 0 ? (
            <>
              {data?.pages.map((l: any, idx1) => (
                <React.Fragment key={`ct-cat-more-pages-${idx1}`}>
                  {l.data.map((c: CompetencyTestType) => {
                    return <CompetencyCard key={c.id} {...c}></CompetencyCard>;
                  })}
                </React.Fragment>
              ))}
              <span ref={ref} className={"edge-infinite"}></span>
            </>
          ) : (
            <Card className="mt-4 border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <div className="bg-muted p-4 rounded-full mb-4">
                  <SearchX className="w-12 h-12 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">Belum Ada Tes</h3>
                <p className="text-muted-foreground max-w-xs">
                  Saat ini belum ada tes yang tersedia untuk kategori ini. Silakan periksa kembali nanti.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </>
  );
};
export default CompetencyCat;